import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import { FormSections, useLocalizeMessage, ModalWindow, Spinner, useToastNotification } from 'libs.nucleus';
import { useBooleanState } from 'libs.react';
import { ParticipantEmailField } from './form_items/participant_email.component';
import { ParticipantLanguageField } from './form_items/participant_language.component';
import { ParticipantNumberField } from './form_items/participant_number.component';
import { createParticipant, getParticipantFormInitialValues } from './helpers/participant_form.helpers';
import { ParticipantInviteModal } from './participant_invite_modal.component';
import { LogEvents } from '../../constants/log_events.constants';
import { getApiErrorMessage } from '../../helpers/api.helpers';
import { getParticipantDuplicateIndexErrorMessage } from '../../helpers/participants.helpers';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useForm } from '../../hooks/use_form';
import { useLumosEventLogger } from '../../hooks/use_lumos_event_logger';
import { useOrgContext } from '../../hooks/use_org_context';
import { useSiteContext } from '../../hooks/use_site_context';
export const ParticipantFormModal = ({ isModalOpen, participant, onClose }) => {
    const translate = useLocalizeMessage();
    const org = useOrgContext();
    const account = useAxonAccountContext();
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const { form, isFieldtouched, onFieldChange, onSelectFieldChange, resetForm } = useForm({
        initialValues: getParticipantFormInitialValues(participant),
    });
    const [isSaving, , , toggleIsSaving] = useBooleanState(false);
    const [createdParticipant, setCreatedParticipant] = useState();
    const [isInviteModalOpen, setIsInviteModalOpen, setIsInviteModalClose] = useBooleanState(false);
    const [agreeToSetupWithoutEmail, setAgreeToSetupWithoutEmail] = useState(false);
    const { addNotification } = useToastNotification();
    const navigate = useNavigate();
    const { logLumosEvent } = useLumosEventLogger();
    const handleCheckboxChange = (checked) => {
        setAgreeToSetupWithoutEmail(checked);
        // Clear email when checkbox is checked
        if (checked) {
            onFieldChange({ target: { name: 'email', value: '' } });
        }
    };
    const onInviteModalDone = (participant) => {
        navigate(`/site/study/${environment?.id}/participants/${participant._id}`);
    };
    const onModalClose = () => {
        onClose();
        resetForm();
    };
    // Check if we're on R5.2+ (version 50200 or higher)
    const isR52OrHigher = org?.c_pinned_version !== undefined && org.c_pinned_version >= 50200;
    // Check if PII is disabled (no_pii = true means hide email)
    // For R5.2+: no_pii flags hide email field and bypass email requirement
    const isSiteNoPii = site?.c_no_pii === true;
    const isOrgNoPii = org?.c_no_pii === true;
    // Email format validation
    // For R5.2+: Skip validation entirely if isSiteNoPii is true
    // For pre-R5.2: Always validate email format if provided
    let emailValidationError = undefined;
    if (isR52OrHigher && isSiteNoPii) {
        // R5.2+ with site no_pii: skip all email validation
        emailValidationError = undefined;
    }
    else if (form?.email && !isEmail(form?.email)) {
        // Validate email format if provided
        emailValidationError = translate('validation.invalid_email');
    }
    else if (isOrgNoPii) {
        emailValidationError = undefined;
    }
    const errors = {
        email: emailValidationError,
        language: !form?.language?.value ? translate('validation.participant_form.language_required') : undefined,
    };
    if (!errors.email && !form?.email) {
        if (isR52OrHigher) {
            if (isSiteNoPii || agreeToSetupWithoutEmail || isOrgNoPii) {
                errors.email = undefined;
            }
            else {
                errors.email = translate('validation.field_required');
            }
        }
        else {
            if (org?.c_requires_econsent_field) {
                errors.email = translate('validation.field_required');
            }
        }
    }
    const formHasErrors = Object.values(errors).some((error) => !!error);
    // Form is valid when there are no errors
    const isFormValid = !formHasErrors;
    const saveForm = async (event) => {
        event.preventDefault();
        if (!isFormValid) {
            return;
        }
        if (!environment) {
            throw new Error('Environment not defined');
        }
        try {
            toggleIsSaving();
            const participant = await createParticipant({
                environment,
                org,
                account,
                site,
                form,
            });
            if (participant) {
                addNotification({
                    title: '',
                    subtitle: translate('info.participant.created'),
                    type: 'success',
                });
                logLumosEvent(LogEvents.PARTICIPANT_CREATION_SUCCESS, {
                    participantId: participant._id,
                });
                const shouldRedirectToEconsent = org?.c_requires_econsent_field && !agreeToSetupWithoutEmail;
                if (shouldRedirectToEconsent) {
                    navigate(`/site/study/${environment?.id}/participants/${participant._id}#documents`);
                    return;
                }
                setCreatedParticipant(participant);
                onModalClose();
                setIsInviteModalOpen();
            }
        }
        catch (e) {
            console.error('error', e);
            let message = getParticipantDuplicateIndexErrorMessage(e, translate);
            if (!message) {
                message = getApiErrorMessage(e);
            }
            logLumosEvent(LogEvents.PARTICIPANT_CREATION_ERROR, {
                errorMessage: message,
            });
            addNotification({
                title: '',
                subtitle: message ?? translate('error.generic'),
                type: 'error',
            });
        }
        finally {
            toggleIsSaving();
        }
    };
    const formSections = [
        {
            title: translate('generic.details'),
            content: (_jsxs("div", { className: 'flex flex-col gap-4', children: [_jsx(ParticipantLanguageField, { form: form, errors: errors, isFieldtouched: isFieldtouched, onSelectFieldChange: onSelectFieldChange }), _jsx(ParticipantEmailField, { form: form, errors: errors, onFieldChange: onFieldChange, onCheckboxChange: handleCheckboxChange, isFieldtouched: isFieldtouched, agreeToSetupWithoutEmail: agreeToSetupWithoutEmail }), _jsx(ParticipantNumberField, { form: form, errors: errors, onFieldChange: onFieldChange, isFieldtouched: isFieldtouched })] })),
        },
    ];
    return (_jsxs(_Fragment, { children: [createdParticipant && isInviteModalOpen && (_jsx(ParticipantInviteModal, { participant: createdParticipant, onClose: setIsInviteModalClose, onDone: () => onInviteModalDone(createdParticipant) })), _jsxs(ModalWindow, { title: translate('label.participant.create'), isOpen: isModalOpen, closeWindow: onModalClose, footerSecondaryActionButtons: [{ label: translate('generic.cancel'), onClick: onModalClose }], footerPrimaryActionButton: {
                    disabled: !isFormValid,
                    label: translate('generic.create', { tense: 'present' }),
                    onClick: saveForm,
                    id: 'create-participant-invite',
                    dataTestId: 'create-participant-invite',
                }, width: 'full', children: [isSaving && _jsx(Spinner, { wrapper: 'full' }), _jsx("form", { autoComplete: 'off', onSubmit: saveForm, className: 'mt-10 mb-24', children: _jsx(FormSections, { sections: formSections }) })] })] }));
};
